import api, { ApiError } from './api';

const extractDateInput = (value) => {
  if (!value) {
    return undefined;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime())
      ? undefined
      : parsed.toISOString().slice(0, 10);
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? undefined
      : value.toISOString().slice(0, 10);
  }

  return undefined;
};

const toNumberOrUndefined = (value) => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

const sanitizeString = (value) => {
  if (value === null || value === undefined) {
    return undefined;
  }

  const trimmed = String(value).trim();
  return trimmed.length ? trimmed : undefined;
};

const normalizeCurrency = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const normalizeLoad = (raw = {}) => {
  if (!raw) {
    return null;
  }

  return {
    id: raw.id,
    companyId: raw.companyId,
    loadNumber: raw.loadNumber,
    status: raw.status || 'scheduled',
    priority: raw.priority || 'medium',
    pickupLocation: raw.pickupLocation || '',
    pickupAddress: raw.pickupAddress || raw.pickupLocation || '',
    pickupDate: raw.pickupDate || '',
    deliveryLocation: raw.deliveryLocation || '',
    deliveryAddress: raw.deliveryAddress || raw.deliveryLocation || '',
    deliveryDate: raw.deliveryDate || '',
    customer: raw.customer || '',
    customerEmail: raw.customerEmail || '',
    customerPhone: raw.customerPhone || '',
    loadType: raw.loadType || 'Dry Van',
    weight: raw.weight ?? null,
    distance: raw.distance ?? null,
    brokerRate: normalizeCurrency(raw.brokerRate),
    carrierAmount: normalizeCurrency(raw.carrierAmount),
    fuelSurcharge: normalizeCurrency(raw.fuelSurcharge),
    accessorials: normalizeCurrency(raw.accessorials),
    tollFees: normalizeCurrency(raw.tollFees),
    otherFees: normalizeCurrency(raw.otherFees),
    revenue: normalizeCurrency(raw.revenue),
    netRevenue: normalizeCurrency(raw.netRevenue),
    driverName: raw.driverName || '',
    driverId: raw.driverId || '',
    vehicleId: raw.vehicleId || '',
    notes: raw.notes || '',
    createdAt: raw.createdAt || null,
    updatedAt: raw.updatedAt || null
  };
};

const buildLoadPayload = (formData = {}) => {
  const payload = {
    loadNumber: sanitizeString(formData.loadNumber),
    customer: sanitizeString(formData.customer),
    customerEmail: sanitizeString(formData.customerEmail),
    customerPhone: sanitizeString(formData.customerPhone),
    pickupLocation: sanitizeString(formData.pickupLocation),
    pickupAddress: sanitizeString(formData.pickupAddress),
    deliveryLocation: sanitizeString(formData.deliveryLocation),
    deliveryAddress: sanitizeString(formData.deliveryAddress),
    pickupDate: extractDateInput(formData.pickupDate),
    deliveryDate: extractDateInput(formData.deliveryDate),
    loadType: sanitizeString(formData.loadType),
    weight: toNumberOrUndefined(formData.weight),
    distance: toNumberOrUndefined(formData.distance),
    brokerRate: toNumberOrUndefined(formData.brokerRate),
    carrierAmount: toNumberOrUndefined(formData.carrierAmount),
    fuelSurcharge: toNumberOrUndefined(formData.fuelSurcharge),
    accessorials: toNumberOrUndefined(formData.accessorials),
    tollFees: toNumberOrUndefined(formData.tollFees),
    otherFees: toNumberOrUndefined(formData.otherFees),
    driverName: sanitizeString(formData.driverName),
    driverId: sanitizeString(formData.driverId),
    vehicleId: sanitizeString(formData.vehicleId),
    status: sanitizeString(formData.status),
    priority: sanitizeString(formData.priority),
    notes: sanitizeString(formData.notes)
  };

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined)
  );
};

class LoadService {
  constructor() {
    this.basePath = '/loads';
  }

  async listLoads(params = {}, { companyId } = {}) {
    try {
      const config = { params };
      if (companyId) {
        config.headers = { 'x-company-id': companyId };
      }

      const response = await api.get(this.basePath, config);
      console.log("loads list response: ", response)
      const loads = Array.isArray(response?.loads)
        ? response?.loads.map((item) => normalizeLoad(item)).filter(Boolean)
        : [];

      console.log("loads list: ", loads)

      return {
        loads,
        stats: response.stats || {
          totalLoads: loads.length,
          totalRevenue: loads.reduce((sum, load) => sum + (load.revenue || 0), 0),
          deliveredCount: loads.filter((load) => load.status === 'delivered').length,
          inTransitCount: loads.filter((load) => load.status === 'in_transit').length,
          scheduledCount: loads.filter((load) => load.status === 'scheduled').length,
          cancelledCount: loads.filter((load) => load.status === 'cancelled').length,
          avgRevenue: loads.length ? Math.round(loads.reduce((sum, load) => sum + (load.revenue || 0), 0) / loads.length) : 0
        },
        pagination: response.pagination || {
          page: 1,
          limit: loads.length,
          total: loads.length,
          totalPages: 1,
          hasMore: false
        }
      };
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  async createLoad(formData, { companyId } = {}) {
    try {
      const payload = buildLoadPayload(formData);
      const config = {};
      if (companyId) {
        config.headers = { 'x-company-id': companyId };
      }

      const response = await api.post(this.basePath, payload, config);
      const created = response?.data?.load || response?.load || response;
      return normalizeLoad(created);
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  async updateLoad(loadId, formData, { companyId } = {}) {
    if (!loadId) {
      throw new Error('Load ID is required to update a load');
    }

    try {
      const payload = buildLoadPayload(formData);
      const config = {};
      if (companyId) {
        config.headers = { 'x-company-id': companyId };
      }

      const response = await api.put(`${this.basePath}/${loadId}`, payload, config);
      const updated = response?.data?.load || response?.load || response;
      return normalizeLoad(updated);
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  async deleteLoad(loadId, { companyId } = {}) {
    if (!loadId) {
      throw new Error('Load ID is required to delete a load');
    }

    try {
      const config = {};
      if (companyId) {
        config.headers = { 'x-company-id': companyId };
      }

      await api.delete(`${this.basePath}/${loadId}`, config);
      return true;
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  async updateLoadStatus(loadId, status, { companyId } = {}) {
    if (!loadId) {
      throw new Error('Load ID is required to update status');
    }

    if (!status) {
      throw new Error('Status value is required');
    }

    try {
      const payload = { status };
      const config = {};
      if (companyId) {
        config.headers = { 'x-company-id': companyId };
      }

      const response = await api.put(`${this.basePath}/${loadId}`, payload, config);
      const updated = response?.data?.load || response?.load || response;
      return normalizeLoad(updated);
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }
}

const loadService = new LoadService();
export default loadService;
